/**
 * Local Feather-style icon set for the payments / bookings / helpdesk screens.
 *
 * NOTE (deviation, flagged — same as src/components/gate/icons.tsx and
 * src/features/account-admin/icons.tsx): the brief asks for Feather icons via
 * '@expo/vector-icons', but that package is not installed anywhere in this
 * workspace and adding npm packages / touching package.json is out of scope.
 * These are geometric approximations of the Feather/Lucide glyphs, built
 * purely from Views (bars, rings, dots, boxes) so they render identically on
 * iOS, Android and web with zero new dependencies. API mirrors Feather:
 * <Icon name="droplet" size={20} color={...} />. Swap one-for-one once the
 * package lands.
 */
import React from 'react';
import { View, type StyleProp, type ViewStyle } from 'react-native';

import { useTheme } from '@/theme';

export type IconName =
  | 'check-circle' | 'clock' | 'calendar' | 'info' | 'x' | 'plus'
  | 'credit-card' | 'smartphone' | 'landmark' | 'refresh-cw'
  | 'user' | 'users' | 'camera' | 'send'
  | 'droplet' | 'zap' | 'shield' | 'arrow-up-circle' | 'alert-octagon' | 'wind' | 'more-horizontal'
  | 'dumbbell' | 'waves' | 'target' | 'sparkles';

const sw = (s: number) => Math.max(1.6, 2 * s);

/** Solid rounded stroke. x/y = top-left before rotation (rotates about centre). */
const bar = (s: number, c: string, x: number, y: number, w: number, h: number, rotate?: string): ViewStyle => ({
  position: 'absolute', left: x * s, top: y * s, width: w * s, height: h * s,
  borderRadius: (Math.min(w, h) * s) / 2, backgroundColor: c,
  ...(rotate ? { transform: [{ rotate }] } : null),
});

const dot = (s: number, c: string, x: number, y: number, d: number): ViewStyle => ({
  position: 'absolute', left: x * s, top: y * s, width: d * s, height: d * s,
  borderRadius: (d * s) / 2, backgroundColor: c,
});

/** Outlined circle. gapTop leaves the top border transparent (refresh arc). */
const ring = (s: number, c: string, x: number, y: number, d: number, gapTop = false): ViewStyle => ({
  position: 'absolute', left: x * s, top: y * s, width: d * s, height: d * s,
  borderRadius: (d * s) / 2, borderWidth: sw(s), borderColor: c,
  ...(gapTop ? { borderTopColor: 'transparent' } : null),
});

/** Outlined rounded rect. */
const box = (s: number, c: string, x: number, y: number, w: number, h: number, r: number): ViewStyle => ({
  position: 'absolute', left: x * s, top: y * s, width: w * s, height: h * s,
  borderRadius: r * s, borderWidth: sw(s), borderColor: c,
});

/** Solid triangle: dir 'up' | 'down' | 'left' | 'right'. */
const wedge = (s: number, c: string, x: number, y: number, w: number, h: number, dir: 'up' | 'down' | 'left' | 'right'): ViewStyle => {
  const base: ViewStyle = { position: 'absolute', left: x * s, top: y * s, width: 0, height: 0 };
  if (dir === 'up') return { ...base, borderLeftWidth: (w / 2) * s, borderRightWidth: (w / 2) * s, borderBottomWidth: h * s, borderLeftColor: 'transparent', borderRightColor: 'transparent', borderBottomColor: c };
  if (dir === 'down') return { ...base, borderLeftWidth: (w / 2) * s, borderRightWidth: (w / 2) * s, borderTopWidth: h * s, borderLeftColor: 'transparent', borderRightColor: 'transparent', borderTopColor: c };
  if (dir === 'right') return { ...base, borderTopWidth: (h / 2) * s, borderBottomWidth: (h / 2) * s, borderLeftWidth: w * s, borderTopColor: 'transparent', borderBottomColor: 'transparent', borderLeftColor: c };
  return { ...base, borderTopWidth: (h / 2) * s, borderBottomWidth: (h / 2) * s, borderRightWidth: w * s, borderTopColor: 'transparent', borderBottomColor: 'transparent', borderRightColor: c };
};

const V = (style: ViewStyle, key: number) => <View key={key} style={style} />;

const GLYPHS: Record<IconName, (s: number, c: string) => React.ReactNode> = {
  'check-circle': (s, c) => [
    V(ring(s, c, 3.5, 3.5, 17), 0),
    V(bar(s, c, 7, 12.2, 5.2, 1.8, '45deg'), 1),
    V(bar(s, c, 10, 13.6, 8.6, 1.8, '-45deg'), 2),
  ],
  'clock': (s, c) => [V(ring(s, c, 4, 4, 16), 0), V(bar(s, c, 11.2, 7.5, 1.7, 5.5), 1), V(bar(s, c, 11.2, 11.4, 4.5, 1.7), 2)],
  'calendar': (s, c) => [
    V(box(s, c, 4, 5, 16, 15.5, 2), 0), V(bar(s, c, 4.8, 9.8, 14.4, 1.7), 1),
    V(bar(s, c, 7.5, 3, 1.8, 4.5), 2), V(bar(s, c, 14.7, 3, 1.8, 4.5), 3),
  ],
  'info': (s, c) => [V(ring(s, c, 3.5, 3.5, 17), 0), V(dot(s, c, 11, 7, 2.3), 1), V(bar(s, c, 11.15, 10.8, 1.8, 6.5), 2)],
  'x': (s, c) => [V(bar(s, c, 5, 11, 14, 2, '45deg'), 0), V(bar(s, c, 5, 11, 14, 2, '-45deg'), 1)],
  'plus': (s, c) => [V(bar(s, c, 5, 11, 14, 2), 0), V(bar(s, c, 11, 5, 2, 14), 1)],
  'credit-card': (s, c) => [V(box(s, c, 3, 5.5, 18, 13.5, 2), 0), V(bar(s, c, 3.8, 9.5, 16.4, 2.4), 1)],
  'smartphone': (s, c) => [V(box(s, c, 7, 3.5, 10, 17, 2.5), 0), V(dot(s, c, 11, 16.8, 2), 1)],
  'landmark': (s, c) => [
    V(bar(s, c, 9.6, 3, 4.2, 4.6, '-45deg'), 0), V(bar(s, c, 10.2, 3, 4.2, 4.6, '45deg'), 1),
    V(bar(s, c, 4, 8.2, 16, 1.7), 2),
    V(bar(s, c, 6, 10.5, 1.7, 8), 3), V(bar(s, c, 11.15, 10.5, 1.7, 8), 4), V(bar(s, c, 16.3, 10.5, 1.7, 8), 5),
    V(bar(s, c, 3.5, 19, 17, 1.8), 6),
  ],
  'refresh-cw': (s, c) => [V(ring(s, c, 3.5, 3.5, 17, true), 0), V(wedge(s, c, 17.5, 3, 4, 3.6, 'down'), 1)],
  'user': (s, c) => [
    V(ring(s, c, 8, 4, 8), 0),
    V({ position: 'absolute', left: 5 * s, top: 14 * s, width: 14 * s, height: 7 * s, borderWidth: sw(s), borderBottomWidth: 0, borderColor: c, borderTopLeftRadius: 7 * s, borderTopRightRadius: 7 * s }, 1),
  ],
  'users': (s, c) => [
    V(ring(s, c, 5, 4.5, 7), 0),
    V({ position: 'absolute', left: 2.5 * s, top: 14 * s, width: 12 * s, height: 6.5 * s, borderWidth: sw(s), borderBottomWidth: 0, borderColor: c, borderTopLeftRadius: 6 * s, borderTopRightRadius: 6 * s }, 1),
    V(ring(s, c, 15.5, 6, 5), 2),
    V({ position: 'absolute', left: 15.5 * s, top: 14.5 * s, width: 6 * s, height: 6 * s, borderTopWidth: sw(s), borderRightWidth: sw(s), borderColor: c, borderTopRightRadius: 6 * s }, 3),
  ],
  'camera': (s, c) => [
    V(box(s, c, 3, 7, 18, 12, 2.5), 0),
    V(box(s, c, 9, 4, 6, 3.4, 1), 1),
    V(ring(s, c, 8.5, 9.5, 7), 2),
  ],
  'send': (s, c) => [
    V(wedge(s, c, 4, 4.5, 16, 15, 'right'), 0),
    V(bar(s, c, 8, 11.9, 8, 1.5, '18deg'), 1),
  ],
  'droplet': (s, c) => [V(wedge(s, c, 9, 3, 6, 8, 'up'), 0), V(ring(s, c, 5, 9, 14), 1)],
  'zap': (s, c) => [V(bar(s, c, 13, 3.5, 2.2, 9.5, '18deg'), 0), V(bar(s, c, 8.6, 11, 2.2, 9.5, '18deg'), 1)],
  'shield': (s, c) => [V({
    position: 'absolute', left: 5 * s, top: 3.5 * s, width: 14 * s, height: 17 * s,
    borderWidth: sw(s), borderColor: c,
    borderTopLeftRadius: 3 * s, borderTopRightRadius: 3 * s,
    borderBottomLeftRadius: 11 * s, borderBottomRightRadius: 11 * s,
  }, 0)],
  'arrow-up-circle': (s, c) => [
    V(ring(s, c, 3.5, 3.5, 17), 0),
    V(bar(s, c, 11.1, 11, 1.8, 6.5), 1),
    V(wedge(s, c, 8.4, 6.5, 7.2, 5, 'up'), 2),
  ],
  'alert-octagon': (s, c) => [V(ring(s, c, 3.5, 3.5, 17), 0), V(bar(s, c, 11.1, 7, 1.8, 6.5), 1), V(dot(s, c, 11, 15.6, 2.2), 2)],
  'wind': (s, c) => [
    V(bar(s, c, 3, 7, 13, 1.7), 0), V(wedge(s, c, 16, 5.6, 3, 2.8, 'up'), 1),
    V(bar(s, c, 3, 12, 17, 1.7), 2),
    V(bar(s, c, 3, 17, 9, 1.7), 3), V(wedge(s, c, 12, 15.6, 3, 2.8, 'up'), 4),
  ],
  'more-horizontal': (s, c) => [V(dot(s, c, 4.7, 10.7, 2.6), 0), V(dot(s, c, 10.7, 10.7, 2.6), 1), V(dot(s, c, 16.7, 10.7, 2.6), 2)],
  'dumbbell': (s, c) => [
    V(box(s, c, 2.5, 8.5, 3.6, 7, 1.2), 0), V(box(s, c, 17.9, 8.5, 3.6, 7, 1.2), 1),
    V(bar(s, c, 6, 11, 12, 2), 2),
  ],
  'waves': (s, c) => [
    V(bar(s, c, 3, 7.5, 8, 1.7, '-14deg'), 0), V(bar(s, c, 11, 7.9, 8, 1.7, '14deg'), 1),
    V(bar(s, c, 3, 13.5, 8, 1.7, '-14deg'), 2), V(bar(s, c, 11, 13.9, 8, 1.7, '14deg'), 3),
  ],
  'target': (s, c) => [V(ring(s, c, 3, 3, 18), 0), V(ring(s, c, 7, 7, 10), 1), V(dot(s, c, 10.5, 10.5, 3), 2)],
  'sparkles': (s, c) => [
    V(wedge(s, c, 8.5, 3, 5, 6, 'down'), 0), V(wedge(s, c, 8.5, 9, 5, 6, 'up'), 1),
    V(dot(s, c, 16.5, 5, 2), 2), V(dot(s, c, 4.5, 15, 2.4), 3),
  ],
};

export function Icon({ name, size = 20, color, style }: {
  name: IconName; size?: number; color?: string; style?: StyleProp<ViewStyle>;
}) {
  const { colors } = useTheme();
  const c = color ?? colors.textSecondary;
  const s = size / 24;
  return (
    <View pointerEvents="none" style={[{ width: size, height: size }, style]}>
      {GLYPHS[name](s, c)}
    </View>
  );
}
